import {
  PspDocument,
  downloadPspDocument,
  getPspDocumentsPage,
} from "@/api/openapi";
import { FilterOptionsGroup } from "@/components/header/header/Header.types";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import {
  ACCOUNTING_PSP_DOCUMENT_TYPES,
  PAYROLL_ACCOUNTING_PSP_DOCUMENT_TYPES,
  PspDocumentType,
  TableColumn,
} from "@/config/types";
import { getFileFromBlobResponse, downloadFile } from "@/config/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

const LIMIT = 30;

export function usePspDocuments() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, selectedMandateId } = useContext(PspContext);
  const isTablet = useIsTablet();

  // -----------------------------------------------------------------------------
  // REFS
  // -----------------------------------------------------------------------------
  const searchBoxMobileRef = useRef<HTMLInputElement>(null);

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [documents, setDocuments] = useState<PspDocument[]>();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState<number>();
  const [isSearchingMobile, setIsSearchingMobile] = useState(false);
  const [filterValues, setFilterValues] = useState<string[]>([]);
  const [currentFilePreview, setCurrentFilePreview] = useState<File>();

  // -----------------------------------------------------------------------------
  // FETCH INITIAL DATA
  // -----------------------------------------------------------------------------
  const actualSearchQuery = searchQuery.length >= 2 ? searchQuery : undefined;
  useEffect(() => {
    if (selectedMandateId) {
      getPspDocumentsPage({
        query: {
          mandate_id: selectedMandateId,
        },
        body: {
          limit: LIMIT,
          offset: LIMIT * page,
          query: actualSearchQuery,
          documentTypes: filterValues as PspDocumentType[],
        },
      }).then(({ data, error }) => {
        if (error) {
          toast(translate("couldntLoadPspDocuments", lang));
        } else {
          setDocuments(data.data);
          setTotalPages(Math.ceil(data.total / LIMIT) || 1);
        }
      });
    }
  }, [
    page,
    selectedMandateId,
    actualSearchQuery,
    filterValues,
    searchQuery,
    lang,
  ]);

  // -----------------------------------------------------------------------------
  // MEMOIZED VALUES
  // -----------------------------------------------------------------------------
  const columns: TableColumn[] = [
    { key: "type", label: translate("type", lang), className: "w-10" },
    {
      key: "documentType",
      label: translate("documentType", lang),
      className: "w-2/6",
    },
    {
      key: "month",
      label: translate("month", lang),
      className: "min-w-30 w-1/6",
    },
    {
      key: "year",
      label: translate("year", lang),
      className: "min-w-16 w-1/6",
    },
    {
      key: "createdAt",
      label: translate("createdAt", lang),
      className: "min-w-30 w-1/6",
    },
    {
      key: "size",
      label: translate("size", lang),
      className: "min-w-24 w-1/6",
    },
    { key: "action", label: translate("action", lang), className: "w-28" },
  ];

  const filterOptionsGroups: FilterOptionsGroup[] = [
    {
      groupLabel: translate("accounting", lang),
      groupOptions: ACCOUNTING_PSP_DOCUMENT_TYPES.map((documentType) => ({
        key: documentType,
        label: translate(documentType, lang),
      })),
    },
    {
      groupLabel: translate("payroll_accounting", lang),
      groupOptions: PAYROLL_ACCOUNTING_PSP_DOCUMENT_TYPES.map(
        (documentType) => ({
          key: documentType,
          label: translate(documentType, lang),
        }),
      ),
    },
  ];

  // -----------------------------------------------------------------------------
  // CALLBACK FUNCTIONS
  // -----------------------------------------------------------------------------
  function onOpenPreview(documentId: string) {
    downloadPspDocument({
      path: { document_id: documentId },
      responseType: "blob",
    }).then((response) => {
      if (response.error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setCurrentFilePreview(getFileFromBlobResponse(response));
      }
    });
  }

  function onClosePreview() {
    setCurrentFilePreview(undefined);
  }

  function onDownload() {
    if (currentFilePreview) {
      downloadFile(currentFilePreview);
      onClosePreview();
    }
  }

  function startSearchingMobile() {
    setIsSearchingMobile(true);
    setTimeout(() => {
      searchBoxMobileRef.current?.focus();
    }, 0);
  }

  return {
    isTablet,
    columns,
    filterOptionsGroups,
    startSearchingMobile,
    onDownload,
    onOpenPreview,
    selectedMandateId,
    setSearchQuery,
    searchQuery,
    setFilterValues,
    filterValues,
    documents,
    lang,
    page,
    totalPages,
    setPage,
    currentFilePreview,
    onClosePreview,
    isSearchingMobile,
    searchBoxMobileRef,
    setIsSearchingMobile,
  };
}
